import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, MapPin, Truck, Camera, History, Copy, 
  Search, Filter, Upload, X, Plus, Edit2, ChevronDown,
  ChevronUp, AlertCircle, CheckCircle, Clock, Printer, ExternalLink
} from 'lucide-react';
import api from '../services/api';

// Obtener la URL base del backend
const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? process.env.REACT_APP_API_URL.replace('/api', '') 
  : '${API_BASE_URL}';

const FleetTrackingPage = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [records, setRecords] = useState([]);
  const [yesterdayRecords, setYesterdayRecords] = useState([]); // Registros del día anterior
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedLocation, setExpandedLocation] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printDate, setPrintDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [stats, setStats] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [newLocation, setNewLocation] = useState({
    nombre: '',
    capacidad_maxima: 10,
    color_hex: '#3B82F6',
    orden: 100
  });
  const [showChangeSummary, setShowChangeSummary] = useState(false); // Para mostrar/ocultar resumen de cambios

  useEffect(() => {
    fetchLocations();
    fetchRecords();
    fetchYesterdayRecords(); // Cargar registros del día anterior
    fetchStats();
    fetchAlerts();
  }, [selectedDate]);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/fleet-tracking/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error al cargar ubicaciones:', error);
    }
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/fleet-tracking/daily/${selectedDate}`);
      setRecords(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar registros:', error);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchYesterdayRecords = async () => {
    try {
      const yesterday = new Date(selectedDate);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      const response = await api.get(`/fleet-tracking/daily/${yesterdayStr}`);
      setYesterdayRecords(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar registros de ayer:', error);
      setYesterdayRecords([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get(`/fleet-tracking/stats/occupancy?fecha=${selectedDate}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/fleet-tracking/alerts?dias=7');
      setAlerts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar alertas:', error);
      setAlerts([]);
    }
  };

  const handleCopyYesterday = async () => {
    if (!window.confirm('¿Copiar registros del día anterior?')) return;
    
    setLoading(true);
    try {
      const response = await api.post('/fleet-tracking/daily/copy-from-yesterday', {
        fecha_nueva: selectedDate
      });
      const data = response.data;
      
      if (data.success) {
        let mensaje = data.fecha_origen 
          ? `✅ ${data.copiados} unidades copiadas desde ${new Date(data.fecha_origen + 'T00:00:00').toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}`
          : `✅ ${data.copiados} unidades registradas desde el catálogo`;
        
        if (data.unidades_nuevas > 0) {
          mensaje += `\n🆕 ${data.unidades_nuevas} unidad${data.unidades_nuevas > 1 ? 'es' : ''} nueva${data.unidades_nuevas > 1 ? 's' : ''} agregada${data.unidades_nuevas > 1 ? 's' : ''} automáticamente`;
        }
        
        alert(mensaje);
        fetchRecords();
        fetchStats();
        fetchAlerts();
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al copiar registros');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRecord = async (record, newUbicacion, newEstatus, observaciones) => {
    try {
      await api.put(`/fleet-tracking/daily/${record.id}`, {
        ubicacion: newUbicacion,
        estatus: newEstatus,
        observaciones,
        registrado_por: 'Usuario'
      });
      
      // Si llegamos aquí, la petición fue exitosa
      fetchRecords();
      fetchStats();
      fetchAlerts(); // Actualizar alertas cuando cambia la ubicación
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAddLocation = async () => {
    if (!newLocation.nombre.trim()) {
      alert('❌ El nombre de la ubicación es requerido');
      return;
    }

    try {
      await api.post('/fleet-tracking/locations', {
        nombre: newLocation.nombre.trim(),
        capacidad_maxima: parseInt(newLocation.capacidad_maxima) || 10,
        color_hex: newLocation.color_hex,
        orden: parseInt(newLocation.orden) || 100,
        activo: true
      });

      // Recargar ubicaciones
      const response = await api.get('/fleet-tracking/locations');
      setLocations(response.data);

      // Limpiar formulario y cerrar modal
      setNewLocation({
        nombre: '',
        capacidad_maxima: 10,
        color_hex: '#3B82F6',
        orden: 100
      });
      setShowAddLocationModal(false);

      alert('✅ Ubicación creada exitosamente');
    } catch (error) {
      console.error('Error creando ubicación:', error);
      alert('❌ Error al crear la ubicación: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation({
      id: location.id,
      nombre: location.nombre,
      capacidad_maxima: location.capacidad_maxima,
      color_hex: location.color_hex,
      orden: location.orden,
      activo: location.activo
    });
    setShowEditLocationModal(true);
  };

  const handleUpdateLocation = async () => {
    if (!editingLocation.nombre.trim()) {
      alert('❌ El nombre de la ubicación es requerido');
      return;
    }

    try {
      await api.put(`/fleet-tracking/locations/${editingLocation.id}`, {
        nombre: editingLocation.nombre.trim(),
        capacidad_maxima: parseInt(editingLocation.capacidad_maxima) || 10,
        color_hex: editingLocation.color_hex,
        orden: parseInt(editingLocation.orden) || 100,
        activo: editingLocation.activo
      });

      // Recargar ubicaciones
      const response = await api.get('/fleet-tracking/locations');
      setLocations(response.data);

      setShowEditLocationModal(false);
      setEditingLocation(null);

      alert('✅ Ubicación actualizada exitosamente');
    } catch (error) {
      console.error('Error actualizando ubicación:', error);
      alert('❌ Error al actualizar la ubicación: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteLocation = async (location) => {
    try {
      // Primer intento de eliminación
      await api.delete(`/fleet-tracking/locations/${location.id}`);

      // Recargar ubicaciones
      const response = await api.get('/fleet-tracking/locations');
      setLocations(response.data);

      alert('✅ Ubicación eliminada exitosamente');
    } catch (error) {
      console.error('Error eliminando ubicación:', error);
      const errorData = error.response?.data;
      
      if (errorData?.requiresConfirmation) {
        // Tiene registros históricos, pedir confirmación
        const confirmDelete = window.confirm(
          `⚠️ ${errorData.message}\n\n` +
          `Registros históricos: ${errorData.historicalCount}\n\n` +
          `${errorData.suggestion}\n\n` +
          `¿Desea eliminar la ubicación del catálogo de todos modos?\n` +
          `(Los registros históricos se mantendrán con el nombre original)`
        );

        if (confirmDelete) {
          try {
            // Segundo intento con force=true
            await api.delete(`/fleet-tracking/locations/${location.id}?force=true`);
            
            // Recargar ubicaciones
            const response = await api.get('/fleet-tracking/locations');
            setLocations(response.data);
            
            alert('✅ Ubicación eliminada del catálogo. Los registros históricos se han preservado.');
          } catch (forceError) {
            console.error('Error al forzar eliminación:', forceError);
            alert('❌ Error al eliminar la ubicación: ' + (forceError.response?.data?.error || forceError.message));
          }
        }
      } else if (errorData?.unitsCount) {
        // Tiene unidades asignadas HOY - Ofrecer moverlas
        const otherLocations = locations.filter(loc => loc.id !== location.id && loc.activo);
        
        if (otherLocations.length === 0) {
          alert('❌ No hay otras ubicaciones disponibles para mover las unidades');
          return;
        }

        // Crear mensaje con opciones
        let message = `⚠️ ${errorData.error}\n\n`;
        message += `Unidades asignadas hoy: ${errorData.unitsCount}\n\n`;
        message += `¿Desea mover todas las unidades a otra ubicación?\n\n`;
        message += `Ubicaciones disponibles:\n`;
        otherLocations.forEach((loc, idx) => {
          message += `${idx + 1}. ${loc.nombre}\n`;
        });
        message += `\nEscriba el número de la ubicación destino (1-${otherLocations.length}) o "Cancelar"`;

        const choice = prompt(message);

        if (!choice || choice.toLowerCase() === 'cancelar') {
          return;
        }

        const selectedIndex = parseInt(choice) - 1;
        
        if (isNaN(selectedIndex) || selectedIndex < 0 || selectedIndex >= otherLocations.length) {
          alert('❌ Opción inválida');
          return;
        }

        const targetLocation = otherLocations[selectedIndex];

        // Confirmar la acción
        const confirmMove = window.confirm(
          `¿Confirma que desea mover las ${errorData.unitsCount} unidades de "${location.nombre}" a "${targetLocation.nombre}" y luego eliminar "${location.nombre}"?`
        );

        if (!confirmMove) return;

        try {
          // Mover todas las unidades
          await api.post('/fleet-tracking/move-units', {
            fromLocation: location.nombre,
            toLocation: targetLocation.nombre,
            fecha: selectedDate
          });

          // Intentar eliminar de nuevo
          await api.delete(`/fleet-tracking/locations/${location.id}`);

          // Recargar ubicaciones y registros
          await fetchLocations();
          await fetchRecords();

          alert(`✅ Se movieron ${errorData.unitsCount} unidades a "${targetLocation.nombre}" y se eliminó la ubicación "${location.nombre}"`);
        } catch (moveError) {
          console.error('Error moviendo unidades:', moveError);
          alert('❌ Error al mover las unidades: ' + (moveError.response?.data?.error || moveError.message));
        }
      } else {
        // Otro error
        alert('❌ Error al eliminar la ubicación: ' + (errorData?.error || error.message));
      }
    }
  };

  const handleGenerateReport = async () => {
    try {
      const response = await api.post('/fleet-tracking/generate-report', {
        fecha: printDate
      });
      
      const data = response.data;
      
      if (data.success) {
        // Abrir el archivo HTML generado desde el backend
        const reportUrl = `http://localhost:3001/api/fleet-tracking/reports/${data.filename}`;
        window.open(reportUrl, '_blank');
        setShowPrintModal(false);
      } else {
        alert('❌ Error al generar el reporte');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al generar el reporte: ' + (error.response?.data?.error || error.message));
    }
  };

  // Filtrar registros por búsqueda
  const filteredRecords = searchTerm
    ? records.filter(r => 
        r.numero_economico?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.placas?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : records;

  // Calcular cambios vs día anterior
  const calculateChanges = () => {
    const changes = {
      nuevas: [],
      removidas: [],
      cambiadas: [],
      sinCambios: []
    };

    // Map de registros de ayer por número económico
    const yesterdayMap = {};
    yesterdayRecords.forEach(rec => {
      yesterdayMap[rec.numero_economico] = rec;
    });

    // Map de registros de hoy por número económico
    const todayMap = {};
    records.forEach(rec => {
      todayMap[rec.numero_economico] = rec;
    });

    // Detectar nuevas y cambiadas
    records.forEach(todayRec => {
      const yesterdayRec = yesterdayMap[todayRec.numero_economico];
      
      if (!yesterdayRec) {
        // Unidad nueva
        changes.nuevas.push(todayRec);
      } else if (yesterdayRec.ubicacion !== todayRec.ubicacion) {
        // Unidad cambió de ubicación
        changes.cambiadas.push({
          ...todayRec,
          ubicacion_anterior: yesterdayRec.ubicacion
        });
      } else {
        changes.sinCambios.push(todayRec);
      }
    });

    // Detectar removidas (estaban ayer pero no hoy)
    yesterdayRecords.forEach(yesterdayRec => {
      if (!todayMap[yesterdayRec.numero_economico]) {
        changes.removidas.push(yesterdayRec);
      }
    });

    return changes;
  };

  const changes = calculateChanges();

  // Determinar el estado de cada unidad para el badge
  const getUnitStatus = (record) => {
    const yesterdayRec = yesterdayRecords.find(r => r.numero_economico === record.numero_economico);
    
    if (!yesterdayRec) {
      return { type: 'nueva', label: 'NUEVA', color: 'bg-green-500' };
    }
    
    if (yesterdayRec.ubicacion !== record.ubicacion) {
      return { 
        type: 'movida', 
        label: `MOVIDA: ${yesterdayRec.ubicacion}`, 
        color: 'bg-yellow-500' 
      };
    }
    
    return null; // Sin cambios
  };

  // Agrupar registros filtrados por ubicación
  const groupedRecords = filteredRecords.reduce((acc, record) => {
    // Si la ubicación es "PATIO 1" y no ha sido ubicada antes, mover a "Sin Ubicación"
    const ubicacion = record.ubicacion === 'PATIO 1' && !yesterdayRecords.find(r => r.numero_economico === record.numero_economico)
      ? 'SIN UBICACIÓN'
      : record.ubicacion;
    
    if (!acc[ubicacion]) {
      acc[ubicacion] = [];
    }
    acc[ubicacion].push(record);
    return acc;
  }, {});

  // Ordenar ubicaciones: "SIN UBICACIÓN" primero, luego las que tienen resultados de búsqueda
  const sortedLocations = searchTerm 
    ? [...locations].sort((a, b) => {
        // "SIN UBICACIÓN" siempre primero
        if (a.nombre === 'SIN UBICACIÓN') return -1;
        if (b.nombre === 'SIN UBICACIÓN') return 1;
        
        const aHasRecords = (groupedRecords[a.nombre] || []).length > 0;
        const bHasRecords = (groupedRecords[b.nombre] || []).length > 0;
        if (aHasRecords && !bHasRecords) return -1;
        if (!aHasRecords && bHasRecords) return 1;
        return 0;
      })
    : (() => {
        // Sin búsqueda: "SIN UBICACIÓN" primero, luego orden normal
        const sinUbicacion = locations.find(l => l.nombre === 'SIN UBICACIÓN');
        const otrasUbicaciones = locations.filter(l => l.nombre !== 'SIN UBICACIÓN');
        return sinUbicacion ? [sinUbicacion, ...otrasUbicaciones] : otrasUbicaciones;
      })();

  // Auto-expandir ubicaciones con resultados cuando hay búsqueda
  useEffect(() => {
    if (searchTerm && filteredRecords.length > 0) {
      // Expandir la primera ubicación que tiene resultados
      const firstLocationWithResults = sortedLocations.find(
        loc => (groupedRecords[loc.nombre] || []).length > 0
      );
      if (firstLocationWithResults) {
        setExpandedLocation(firstLocationWithResults.nombre);
      }
    } else if (!searchTerm) {
      setExpandedLocation(null);
    }
  }, [searchTerm, filteredRecords.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full mx-auto"
      >
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 rounded-2xl shadow-lg">
                <MapPin className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-800">
                  Registro Diario de Tractocamiones
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Control de ubicación y estatus de tractocamiones en patio y talleres
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowPrintModal(true)}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl shadow-lg"
              >
                <Printer className="w-5 h-5" />
                Imprimir Reporte
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCopyYesterday}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl shadow-lg"
              >
                <Copy className="w-5 h-5" />
                Copiar Ayer
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddLocationModal(true)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-lg"
              >
                <MapPin className="w-5 h-5" />
                Nueva Ubicación
              </motion.button>
            </div>
          </div>

          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar unidad o placas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-2 bg-blue-50 px-4 py-3 rounded-xl">
              <Truck className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {filteredRecords.length} unidades {searchTerm && `(de ${records.length})`}
              </span>
            </div>
          </div>

          {/* Alertas - Unidades con tiempo prolongado en taller */}
          {alerts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setShowAlerts(!showAlerts)}
                className="w-full p-4 flex items-center justify-between hover:bg-red-100/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  <div className="text-left">
                    <h3 className="text-lg font-bold text-red-900">
                      ⚠️ Alertas de Mantenimiento
                    </h3>
                    <p className="text-sm text-red-700">
                      {alerts.length} {alerts.length === 1 ? 'unidad lleva' : 'unidades llevan'} más de 7 días en taller
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                    {alerts.length}
                  </span>
                  {showAlerts ? (
                    <ChevronUp className="w-5 h-5 text-red-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-red-600" />
                  )}
                </div>
              </button>

              <AnimatePresence>
                {showAlerts && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {alerts.map((alert) => {
                          // Buscar el registro completo de la unidad
                          const fullRecord = records.find(r => r.numero_economico === alert.numero_economico);
                          const changeStatus = getUnitStatus(fullRecord || alert);
                          
                          return (
                            <TruckCard
                              key={alert.id}
                              record={fullRecord || {
                                id: alert.id,
                                numero_economico: alert.numero_economico,
                                tipo_unidad: alert.tipo_unidad,
                                ubicacion: alert.ubicacion,
                                estatus: alert.estatus,
                                observaciones: alert.observaciones,
                                fotos: []
                              }}
                              locations={locations}
                              onUpdate={handleUpdateRecord}
                              isAlert={true}
                              diasEnUbicacion={alert.dias_en_ubicacion}
                              navigate={navigate}
                              changeStatus={changeStatus}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Resumen de Cambios vs Día Anterior */}
          {yesterdayRecords.length > 0 && (changes.nuevas.length > 0 || changes.removidas.length > 0 || changes.cambiadas.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-xl overflow-hidden mt-4"
            >
              <button
                onClick={() => setShowChangeSummary(!showChangeSummary)}
                className="w-full p-4 flex items-center justify-between hover:bg-blue-100/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <History className="w-6 h-6 text-blue-600" />
                  <div className="text-left">
                    <h3 className="text-lg font-bold text-blue-900">
                      📊 Cambios vs Día Anterior
                    </h3>
                    <p className="text-sm text-blue-700">
                      {changes.nuevas.length + changes.removidas.length + changes.cambiadas.length} cambios detectados
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {changes.nuevas.length > 0 && (
                    <span className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      +{changes.nuevas.length}
                    </span>
                  )}
                  {changes.cambiadas.length > 0 && (
                    <span className="bg-yellow-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      ~{changes.cambiadas.length}
                    </span>
                  )}
                  {changes.removidas.length > 0 && (
                    <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                      -{changes.removidas.length}
                    </span>
                  )}
                  {showChangeSummary ? (
                    <ChevronUp className="w-5 h-5 text-blue-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-blue-600" />
                  )}
                </div>
              </button>

              <AnimatePresence>
                {showChangeSummary && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Unidades Nuevas */}
                      {changes.nuevas.length > 0 && (
                        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                          <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                            <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                              +{changes.nuevas.length}
                            </span>
                            Nuevas
                          </h4>
                          <ul className="space-y-1 text-sm text-green-800">
                            {changes.nuevas.slice(0, 5).map(rec => (
                              <li key={rec.id} className="truncate">• {rec.numero_economico}</li>
                            ))}
                            {changes.nuevas.length > 5 && (
                              <li className="text-xs italic">...y {changes.nuevas.length - 5} más</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Unidades que Cambiaron */}
                      {changes.cambiadas.length > 0 && (
                        <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                          <h4 className="font-bold text-yellow-900 mb-2 flex items-center gap-2">
                            <span className="bg-yellow-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                              ~{changes.cambiadas.length}
                            </span>
                            Cambiaron de Ubicación
                          </h4>
                          <ul className="space-y-1 text-sm text-yellow-800">
                            {changes.cambiadas.slice(0, 5).map(rec => (
                              <li key={rec.id} className="truncate">
                                • {rec.numero_economico}: {rec.ubicacion_anterior} → {rec.ubicacion}
                              </li>
                            ))}
                            {changes.cambiadas.length > 5 && (
                              <li className="text-xs italic">...y {changes.cambiadas.length - 5} más</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Unidades Removidas */}
                      {changes.removidas.length > 0 && (
                        <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                          <h4 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                            <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                              -{changes.removidas.length}
                            </span>
                            Ya No Están Hoy
                          </h4>
                          <ul className="space-y-1 text-sm text-red-800">
                            {changes.removidas.slice(0, 5).map(rec => (
                              <li key={rec.id} className="truncate">• {rec.numero_economico} ({rec.ubicacion})</li>
                            ))}
                            {changes.removidas.length > 5 && (
                              <li className="text-xs italic">...y {changes.removidas.length - 5} más</li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>

        {/* Estadísticas de Ocupación */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-2xl shadow-lg p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">{stat.ubicacion}</span>
                <span className="text-2xl font-bold" style={{ color: stat.color_hex }}>
                  {stat.unidades}
                </span>
              </div>
              
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${Math.min(stat.porcentaje, 100)}%`,
                    backgroundColor: stat.color_hex
                  }}
                />
              </div>
              
              <div className="text-xs text-gray-500">
                {stat.capacidad_maxima > 0 
                  ? `${stat.porcentaje}% ocupado (${stat.unidades}/${stat.capacidad_maxima})`
                  : `${stat.unidades} unidades`
                }
              </div>
            </motion.div>
          ))}
        </div>

        {/* Vista por Ubicaciones */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-3xl shadow-xl p-20 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Cargando registros...</p>
            </div>
          ) : searchTerm && filteredRecords.length === 0 ? (
            <div className="bg-white rounded-3xl shadow-xl p-20 text-center">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">No se encontraron resultados</h3>
              <p className="text-gray-500">
                No hay unidades que coincidan con "{searchTerm}"
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Limpiar búsqueda
              </button>
            </div>
          ) : sortedLocations.map((location) => {
            const locationRecords = groupedRecords[location.nombre] || [];
            const isExpanded = expandedLocation === location.nombre;
            const hasResults = locationRecords.length > 0;
            
            // No mostrar ubicaciones vacías cuando hay búsqueda activa
            if (searchTerm && !hasResults) {
              return null;
            }
            
            return (
              <motion.div
                key={location.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden ${
                  searchTerm && hasResults ? 'ring-2 ring-blue-400' : ''
                }`}
              >
                <div className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                  <div 
                    onClick={() => setExpandedLocation(isExpanded ? null : location.nombre)}
                    className="flex items-center gap-3 flex-1 cursor-pointer"
                  >
                    <div
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${location.color_hex}20` }}
                    >
                      <MapPin className="w-6 h-6" style={{ color: location.color_hex }} />
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-800">
                        {location.nombre}
                        {searchTerm && hasResults && (
                          <span className="ml-2 text-sm font-normal text-blue-600">
                            ✓ Contiene resultados
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-gray-500">{locationRecords.length} unidades</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditLocation(location);
                      }}
                      className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                      title="Editar ubicación"
                    >
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteLocation(location);
                      }}
                      className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                      title="Eliminar ubicación"
                    >
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    
                    <div 
                      onClick={() => setExpandedLocation(isExpanded ? null : location.nombre)}
                      className="cursor-pointer"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400 ml-2" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400 ml-2" />
                      )}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-4 bg-gray-50 border-t">
                    {locationRecords.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No hay unidades en esta ubicación</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {locationRecords.map((record) => {
                          const changeStatus = getUnitStatus(record);
                          
                          return (
                            <TruckCard
                              key={record.id}
                              record={record}
                              locations={locations}
                              onUpdate={handleUpdateRecord}
                              highlighted={searchTerm}
                              navigate={navigate}
                              changeStatus={changeStatus}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Modal de Selección de Fecha para Imprimir */}
        <AnimatePresence>
          {showPrintModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowPrintModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 rounded-2xl">
                      <Printer className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      Generar Reporte
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowPrintModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="mb-6">
                  <p className="text-gray-600 mb-4">
                    Seleccione la fecha del registro que desea imprimir:
                  </p>
                  
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="date"
                      value={printDate}
                      onChange={(e) => setPrintDate(e.target.value)}
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
                    />
                  </div>

                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Información del reporte:</p>
                        <ul className="list-disc list-inside space-y-1 text-blue-700">
                          <li>Se generará un archivo HTML imprimible</li>
                          <li>Incluye todas las unidades registradas</li>
                          <li>Con firmas oficiales y datos completos</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleGenerateReport}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-4 rounded-xl font-semibold shadow-lg flex items-center justify-center gap-2"
                  >
                    <Printer className="w-5 h-5" />
                    Generar Reporte
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowPrintModal(false)}
                    className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-700 py-4 rounded-xl font-semibold"
                  >
                    Cancelar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {/* Modal Nueva Ubicación */}
          {showAddLocationModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowAddLocationModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 rounded-2xl">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      Nueva Ubicación
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowAddLocationModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4 mb-6">
                  {/* Nombre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de la Ubicación *
                    </label>
                    <input
                      type="text"
                      value={newLocation.nombre}
                      onChange={(e) => setNewLocation({ ...newLocation, nombre: e.target.value })}
                      placeholder="Ej: TALLER 3, PATIO NORTE, etc."
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Capacidad */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Capacidad Máxima
                    </label>
                    <input
                      type="number"
                      value={newLocation.capacidad_maxima}
                      onChange={(e) => setNewLocation({ ...newLocation, capacidad_maxima: e.target.value })}
                      placeholder="10"
                      min="1"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Número máximo de unidades permitidas
                    </p>
                  </div>

                  {/* Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Color de Identificación
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={newLocation.color_hex}
                        onChange={(e) => setNewLocation({ ...newLocation, color_hex: e.target.value })}
                        className="w-16 h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={newLocation.color_hex}
                        onChange={(e) => setNewLocation({ ...newLocation, color_hex: e.target.value })}
                        placeholder="#3B82F6"
                        className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Color para identificar visualmente la ubicación
                    </p>
                  </div>

                  {/* Orden */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Orden de Visualización
                    </label>
                    <input
                      type="number"
                      value={newLocation.orden}
                      onChange={(e) => setNewLocation({ ...newLocation, orden: e.target.value })}
                      placeholder="100"
                      min="0"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Menor número = aparece primero en la lista
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAddLocation}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 rounded-xl font-semibold shadow-lg flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Crear Ubicación
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowAddLocationModal(false)}
                    className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-700 py-4 rounded-xl font-semibold"
                  >
                    Cancelar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal Editar Ubicación */}
        <AnimatePresence>
          {showEditLocationModal && editingLocation && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowEditLocationModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-blue-100 rounded-2xl">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-800">Editar Ubicación</h2>
                    <p className="text-gray-500">Modifica la información de la ubicación</p>
                  </div>
                </div>

                {/* Formulario */}
                <div className="space-y-5">
                  {/* Nombre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre de la Ubicación *
                    </label>
                    <input
                      type="text"
                      value={editingLocation.nombre}
                      onChange={(e) => setEditingLocation({ ...editingLocation, nombre: e.target.value })}
                      placeholder="Ej: TALLER 3, PATIO NORTE, etc."
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Capacidad */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Capacidad Máxima
                    </label>
                    <input
                      type="number"
                      value={editingLocation.capacidad_maxima}
                      onChange={(e) => setEditingLocation({ ...editingLocation, capacidad_maxima: e.target.value })}
                      placeholder="10"
                      min="1"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Número máximo de unidades permitidas
                    </p>
                  </div>

                  {/* Color */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Color de Identificación
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={editingLocation.color_hex}
                        onChange={(e) => setEditingLocation({ ...editingLocation, color_hex: e.target.value })}
                        className="w-16 h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={editingLocation.color_hex}
                        onChange={(e) => setEditingLocation({ ...editingLocation, color_hex: e.target.value })}
                        placeholder="#3B82F6"
                        className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Color para identificar visualmente la ubicación
                    </p>
                  </div>

                  {/* Orden */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Orden de Visualización
                    </label>
                    <input
                      type="number"
                      value={editingLocation.orden}
                      onChange={(e) => setEditingLocation({ ...editingLocation, orden: e.target.value })}
                      placeholder="100"
                      min="0"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Menor número = aparece primero en la lista
                    </p>
                  </div>

                  {/* Estado Activo */}
                  <div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingLocation.activo}
                        onChange={(e) => setEditingLocation({ ...editingLocation, activo: e.target.checked })}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Ubicación activa (visible en el sistema)
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleUpdateLocation}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 rounded-xl font-semibold shadow-lg flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Actualizar Ubicación
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowEditLocationModal(false);
                      setEditingLocation(null);
                    }}
                    className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-700 py-4 rounded-xl font-semibold"
                  >
                    Cancelar
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

// Componente de Card de Unidad
const TruckCard = ({ record, locations, onUpdate, highlighted, isAlert, diasEnUbicacion, navigate, changeStatus }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [editing, setEditing] = useState(false);
  const [ubicacion, setUbicacion] = useState(record.ubicacion);
  const [estatus, setEstatus] = useState(record.estatus);
  const [obs, setObs] = useState(record.observaciones || '');
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photos, setPhotos] = useState(record.fotos || []);
  const [uploading, setUploading] = useState(false);

  const estatusOptions = ['Operativo', 'En Mantenimiento', 'Fuera de Servicio', 'Esperando Refacciones', 'Estacionado'];
  
  const estatusColors = {
    'Operativo': 'bg-green-100 text-green-800',
    'En Mantenimiento': 'bg-yellow-100 text-yellow-800',
    'Fuera de Servicio': 'bg-red-100 text-red-800',
    'Esperando Refacciones': 'bg-orange-100 text-orange-800',
    'Estacionado': 'bg-blue-100 text-blue-800'
  };

  const handleSave = () => {
    onUpdate(record, ubicacion, estatus, obs);
    setEditing(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamaño (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('❌ La foto no debe superar 10MB');
      return;
    }

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      alert('❌ Solo se permiten archivos de imagen');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('descripcion', `Foto de ${record.numero_economico}`);
    formData.append('uploaded_by', 'Usuario');

    try {
      const response = await api.post(`/fleet-tracking/daily/${record.id}/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;
      
      if (data.success) {
        alert('✅ Foto subida exitosamente');
        // Actualizar lista de fotos
        setPhotos([...photos, data.photo]);
      } else {
        alert('❌ Error al subir la foto');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al subir la foto');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;

    try {
      await api.delete(`/fleet-tracking/photos/${photoId}`);

      // Si llegamos aquí, la petición fue exitosa
      alert('✅ Foto eliminada');
      setPhotos(photos.filter(p => p.id !== photoId));
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al eliminar la foto');
    }
  };

  const handleDownloadPhoto = async (photoUrl, fileName) => {
    try {
      const response = await api.get(photoUrl, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error al descargar:', error);
      alert('❌ Error al descargar la foto');
    }
  };

  const fileInputRef = React.createRef();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-all ${
        highlighted ? 'ring-2 ring-blue-400 shadow-blue-200' : ''
      } ${isAlert ? 'ring-2 ring-red-400 border-l-4 border-red-500' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 
              onClick={() => navigate && navigate(`/trucks/${record.truck_id || record.id}`)}
              className="text-lg font-bold text-gray-800 hover:text-blue-600 cursor-pointer transition-colors"
            >
              {record.numero_economico}
            </h4>
            {navigate && (
              <button
                onClick={() => navigate(`/trucks/${record.truck_id || record.id}`)}
                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1 rounded transition-colors"
                title="Ver detalles de la unidad"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
            )}
            {changeStatus && (
              <span 
                className={`${
                  changeStatus.type === 'nueva' ? 'bg-green-500' : 
                  changeStatus.type === 'movida' ? 'bg-yellow-500' : 'bg-gray-500'
                } text-white text-xs font-bold px-2 py-0.5 rounded ${
                  changeStatus.type === 'nueva' ? 'animate-pulse' : ''
                }`}
                title={changeStatus.type === 'movida' ? `Antes estaba en: ${changeStatus.label.split(': ')[1]}` : ''}
              >
                {changeStatus.label}
              </span>
            )}
            {isAlert && diasEnUbicacion && (
              <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {diasEnUbicacion} días
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">{record.tipo_unidad}</p>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${estatusColors[record.estatus] || 'bg-gray-100 text-gray-800'}`}>
          {record.estatus}
        </span>
      </div>

      <div className="space-y-2 text-sm text-gray-600 mb-3">
        <div className="flex items-center gap-2">
          <span className="font-medium">Serie:</span>
          <span>{record.serie || 'N/A'}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium">Placas:</span>
          <span>{record.placas || 'N/A'}</span>
        </div>
        {record.observaciones && (
          <div className="flex items-start gap-2 mt-2">
            <span className="font-medium">Obs:</span>
            <span className="text-xs italic">{record.observaciones}</span>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2 border-t pt-3">
          <select
            value={ubicacion}
            onChange={(e) => setUbicacion(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          >
            {locations.map(loc => (
              <option key={loc.id} value={loc.nombre}>{loc.nombre}</option>
            ))}
          </select>
          
          <select
            value={estatus}
            onChange={(e) => setEstatus(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm"
          >
            {estatusOptions.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          
          <textarea
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            placeholder="Observaciones..."
            className="w-full px-3 py-2 border rounded-lg text-sm"
            rows="2"
          />
          
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="flex-1 bg-blue-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              Guardar
            </button>
            <button
              onClick={() => setEditing(false)}
              className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={() => setEditing(true)}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-700 py-2 rounded-lg text-sm font-medium hover:bg-blue-100"
          >
            <Edit2 className="w-4 h-4" />
            Editar
          </button>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handlePhotoUpload}
            accept="image/*"
            style={{ display: 'none' }}
          />
          
          <button
            onClick={() => fileInputRef.current.click()}
            disabled={uploading}
            className="px-3 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed relative"
            title="Subir foto"
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-purple-700 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Camera className="w-4 h-4" />
                {photos.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {photos.length}
                  </span>
                )}
              </>
            )}
          </button>
          
          {photos.length > 0 && (
            <button
              onClick={() => setShowPhotoModal(true)}
              className="px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm font-medium"
            >
              Ver fotos
            </button>
          )}
        </div>
      )}

      {/* Modal de Fotos */}
      <AnimatePresence>
        {showPhotoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowPhotoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-800">
                    Fotos de {record.numero_economico}
                  </h3>
                  <button
                    onClick={() => setShowPhotoModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {photos.map((photo) => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={`${API_BASE_URL}${photo.foto_url}`}
                        alt={photo.descripcion}
                        className="w-full h-48 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleDownloadPhoto(
                            photo.foto_url,
                            `${record.numero_economico}_${new Date(photo.uploaded_at).toLocaleDateString('es-MX').replace(/\//g, '-')}.jpg`
                          )}
                          className="opacity-0 group-hover:opacity-100 bg-blue-500 text-white px-3 py-1 rounded-lg text-sm font-medium hover:bg-blue-600 transition-opacity flex items-center gap-1"
                        >
                          <Upload className="w-4 h-4 rotate-180" />
                          Descargar
                        </button>
                        <button
                          onClick={() => handleDeletePhoto(photo.id)}
                          className="opacity-0 group-hover:opacity-100 bg-red-500 text-white px-3 py-1 rounded-lg text-sm font-medium hover:bg-red-600 transition-opacity"
                        >
                          Eliminar
                        </button>
                      </div>
                      {photo.descripcion && (
                        <p className="text-xs text-gray-600 mt-1 text-center">
                          {photo.descripcion}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                
                {photos.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No hay fotos disponibles
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FleetTrackingPage;

